import { useEffect, useMemo, useRef, useState } from "react";

// "YYYY-MM" -> "June 2026". Built from a UTC date so a negative local offset can't
// roll the 1st back into the previous month and mislabel the whole run.
export function monthLabel(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  if (!y || !m) return ym;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-GB", {
    month: "long", year: "numeric", timeZone: "UTC",
  });
}

// Last day of "YYYY-MM" as an ISO date. Day 0 of the NEXT month is this month's last
// day, which is leap-year correct without a table.
export function monthEndISO(ym: string): string {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

// "2026-06-30" -> "30 June 2026", matching the Excel header's "%d %B %Y".
export function prettyDate(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric", timeZone: "UTC",
  });
}

// The months offered, newest first. The top of the range is DECEMBER of the later of
// the current year and the ledger's last year, so every month of the current year is
// always selectable — finance closes a month after it ends, and a ledger loaded early
// in the year would otherwise hide the months they are about to close. A month past
// the data is harmless: it trims nothing and only sets the as-of label.
//
// The tail is capped at 24 back (2 years) because a consolidated CRI ledger can span
// many years (Tokio's runs from 2019) and a 78-entry dropdown buries the handful of
// months anyone actually closes. The sidebar's Transaction Period dates cover older.
//
// The ledger's FIRST date deliberately does NOT truncate the list. It used to, on the
// reasoning that an earlier month "would reconcile to an empty window" — but that is
// only true of the CRI book. The insurer statement routinely predates the CRI extract
// (AL WATHBA: CRI starts 2026-06-02, statement 2026-04-01), so the guard hid months
// finance can legitimately close: any insurer whose CRI export covered just the current
// quarter collapsed to "June onwards". Reconciling an earlier month against a statement
// that reaches back is exactly the job, and an over-offered month is harmless.
const MONTHS_BACK = 24;

export function monthOptions(lastISO: string | null): string[] {
  const out: string[] = [];
  const now = new Date();
  const last = lastISO ? new Date(lastISO + "T00:00:00Z") : now;
  // Start at December of the top year, so Jan–Dec of the current year are all present.
  let y = Math.max(now.getUTCFullYear(), last.getUTCFullYear());
  let m = 11;
  // Floor = MONTHS_BACK before the ledger's end, but never later than January of the
  // current year, so the whole current year is always offered.
  let fy = last.getUTCFullYear();
  let fm = last.getUTCMonth() - MONTHS_BACK;
  while (fm < 0) { fm += 12; fy -= 1; }
  if (fy > now.getUTCFullYear() || (fy === now.getUTCFullYear() && fm > 0)) {
    fy = now.getUTCFullYear();
    fm = 0;
  }
  while (y > fy || (y === fy && m >= fm)) {
    out.push(`${y}-${String(m + 1).padStart(2, "0")}`);
    m -= 1;
    if (m < 0) { m = 11; y -= 1; }
  }
  return out;
}

// A month ending before the From in force leaves no row in the window, and that empty
// run reads RECONCILED (Dubai Dec 2024 against its 2025-01-01 start: Difference 0.00).
// Such months stay listed so the range is stable, but cannot be chosen.
export function monthBeforeFrom(ym: string, activeFrom: string): boolean {
  return !!activeFrom && monthEndISO(ym) < activeFrom;
}

interface Props {
  open: boolean;
  // The CRI ledger's own first/last document date, already fetched for the preview.
  criSpan: { first: string | null; last: string | null };
  // The insurer statement's span. The as-of cutoff applies to BOTH books, so this is
  // what lets the dialog name which side loses rows instead of guessing from the CRI
  // alone. Nulls when it could not be read (unknown insurer, multi-file, odd sheet).
  insurerSpan: { first: string | null; last: string | null };
  // The Transaction Period "From" in force, "" when unset. The dialog must SAY so: From
  // is honoured under Automatic exactly as it is under a specific month (period_override
  // bounds period_end only, never period_from), so "Reconciles everything" was a false
  // claim whenever a From was in force. Verified on Emirates: a From of 2020-01-01
  // reconciles from 2020-01-01 under Automatic and stays 2020-01-01 through every month
  // pick.
  periodFrom?: string;
  // What UploadPage SEEDED the From box with for this insurer, and where that seed came
  // from — "" / "none" when it seeded nothing. Only UploadPage knows this, so it has to
  // be passed: the box holds a bare date either way, and calling a system-set floor
  // "your From date" is the wording finance flagged. Compared by VALUE, so a user who
  // overtypes the seed is correctly credited with their own date again.
  seededFrom?: string;
  seededFromSource?: "cri" | "layout" | "none";
  // Whether the engine's cri_period_floor will apply to this insurer. Mirrors the
  // insurers API's derive_period_from_cri, which reads the SAME flag the floor keys
  // off — including its default-ON, so an insurer that never declares the key still
  // reports true. It is false for a layout with default_period_from (Dubai), whose
  // floor is PINNED and survives a cleared From; that is exactly right here, because
  // the notice below offers clearing From as the way to admit the earlier rows.
  derivePeriodFromCri?: boolean;
  // True while the speculative run kicked off behind this dialog is still going.
  running: boolean;
  // Live engine stage text, so the wait is legible instead of a blank modal.
  stageText?: string;
  // "" = Automatic (no override); "YYYY-MM" = reconcile as at that month-end.
  onConfirm: (reconMonth: string) => void;
  onCancel: () => void;
}

// Shown immediately after Run, over the reconciliation already in flight: the engine
// works while finance answers, so the perceived wait is shorter than asking first.
// AUTOMATIC is the default and sends NO override, which keeps that path byte-identical
// to a pre-feature run.
export default function ReconMonthDialog(
  { open, criSpan, insurerSpan, periodFrom, seededFrom, seededFromSource,
    derivePeriodFromCri, running, stageText, onConfirm, onCancel }: Props
) {
  const [auto, setAuto] = useState(true);
  const [month, setMonth] = useState("");
  const confirmRef = useRef<HTMLButtonElement>(null);

  // The tail is measured from the LATEST end across both books, so a statement that
  // runs past the CRI extract cannot shorten the history on offer.
  const spanLast = [criSpan.last, insurerSpan.last].filter(Boolean).sort().slice(-1)[0] ?? null;
  const months = useMemo(() => monthOptions(spanLast), [spanLast]);

  // Default the manual pick to the month the CRI ledger ends in — the CRI defines the
  // reconciliation scope, so its last month stays the answer finance almost always
  // wants even though the offered RANGE now spans both books.
  // A default that ends before the From would open on a month that cannot be chosen;
  // the From's own month is the earliest that can.
  useEffect(() => {
    if (month || !months.length) return;
    const pref = criSpan.last ? criSpan.last.slice(0, 7) : months[0];
    const fromMonth = (periodFrom ?? "").slice(0, 7);
    setMonth(monthBeforeFrom(pref, periodFrom ?? "") && months.includes(fromMonth)
      ? fromMonth : pref);
  }, [months, criSpan.last, month, periodFrom]);

  useEffect(() => { if (open) confirmRef.current?.focus(); }, [open]);

  // Esc cancels, the convention every modal owes its user. Capture phase so the
  // dialog answers before anything underneath it does.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { e.stopPropagation(); onCancel(); }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [open, onCancel]);

  if (!open) return null;

  const chosen = auto ? "" : month;
  const cutoff = chosen ? monthEndISO(chosen) : (criSpan.last ?? "");
  // The as-of cutoff applies to BOTH books, so each is judged against its OWN span.
  // Reading only the CRI span was the original bug: Orient UNB April's CRI ends 30-Apr
  // (nothing to trim) while the SOA carries 01-May and 05-May, so the dialog promised
  // "no rows are excluded" and the run then excluded both.
  const cutCri = !!(chosen && criSpan.last && monthEndISO(chosen) < criSpan.last);
  const cutIns = !!(chosen && insurerSpan.last && monthEndISO(chosen) < insurerSpan.last);
  const trims = cutCri || cutIns;
  // "Nothing is excluded" may only be asserted when BOTH spans were readable and both
  // clear the cutoff. A null span is unknown, not empty — with one missing we can still
  // report a positive (that side proves itself) but never the negative.
  const bothKnown = !!(criSpan.last && insurerSpan.last);
  // Automatic sends no override, which is precisely the condition that switches the
  // engine's cri_period_floor ON — so "reconciles everything" was a flat claim in the
  // one mode where the floor is most reliably applied. Warn ONLY when the floor both
  // applies AND has something to bite on: statement rows genuinely dated before the
  // CRI ledger's first date. Nulls are unknown, not empty, so an unreadable span stays
  // silent rather than crying wolf (the same guard bothKnown makes for the ceiling).
  // Date-based and therefore ADVISORY: a span starting earlier does not prove rows sit
  // there (measured: 9 of 15 outstanding insurers have zero), but the alternative is
  // pre-parsing the statement to count them, which would put real latency on the
  // upload path for one sentence.
  // A From the USER chose wins over cri_period_floor in the engine (the floor only fills
  // in when the caller passed no period_from), so the floor notice retires for one — the
  // dialog must not warn about a floor that is not in force, nor offer "clear the From
  // filter" for a bound the user set on purpose. A SEEDED From is different: its value
  // IS the floor, so the notice must stand (see fromSource below).
  const activeFrom = periodFrom ?? "";
  // WHERE the From in force came from. The box holds a bare date whichever way it was
  // filled, so provenance can only come from the seed UploadPage applied: equal to the
  // seed = still the system's, anything else (including a date the user typed that the
  // seed never offered) = the user's. Finance's report was that the dialog said "your
  // From date" for a floor they never set.
  //  - "cri": derive_period_from_cri seeded the CRI ledger's own earliest doc date, the
  //    window cri_period_floor applies server-side anyway. Clearable.
  //  - "layout": the layout declares default_period_from (Dubai 2025-01-01). PINNED —
  //    the engine keeps this floor even when the box is cleared (_floor_pinned), so it
  //    must never be described as clearable.
  //  - "user": typed in the sidebar, or overtyped on top of a seed.
  const fromSource: "cri" | "layout" | "user" | "none" =
    !activeFrom ? "none"
    : (seededFrom && activeFrom === seededFrom && seededFromSource && seededFromSource !== "none")
      ? seededFromSource
      : "user";
  // How the From is attributed in prose. Parenthetical so it reads the same in both the
  // Automatic and Specific-month branches.
  const fromAttribution =
    fromSource === "cri" ? "the CRI ledger's start, applied automatically"
    : fromSource === "layout" ? "this insurer's standard start date, applied automatically"
    : "your Transaction Period From";
  // The floor still BITES under a system seed: the seeded date IS the floor, so pre-CRI
  // statement rows are held in the opening exactly as they are with an empty box. Only a
  // From the USER chose retires the notice — pointing them at "clear the From filter"
  // for a bound they set on purpose is the case that guard exists for. A "layout" seed
  // is pinned and cannot be cleared away, so it gets its own wording below.
  const floorHides = fromSource !== "user"
    && !!(derivePeriodFromCri && insurerSpan.first && criSpan.first
          && insurerSpan.first < criSpan.first);
  // ONE definition for both modes: the floor applies identically under Automatic and a
  // specific month (period_override bounds period_end only, never period_from), so the
  // two branches must never drift into telling finance different stories about it.
  // "Held in the opening", never "excluded": these rows ARE counted — they fold into the
  // opening balance via the engine's brought-forward term and are listed in full on the
  // Other tab / Others sheet. Calling them excluded would send finance hunting for rows
  // that are already in the export.
  // The remedy differs by provenance: a "cri" floor is clearable, a PINNED layout floor
  // is not (period_from_cleared is ignored for it), so offering "clear the From filter"
  // there would send finance chasing a control that cannot admit those rows.
  const floorNote = floorHides ? (
    <> Statement rows before {prettyDate(criSpan.first!)} are <strong>held in the opening
       balance</strong> and listed under <strong>Other</strong>
       {fromSource === "layout"
         ? <> for this insurer.</>
         : <> — clear the <strong>From</strong> filter to reconcile them too.</>}</>
  ) : null;
  // Which side loses rows, for wording that tells finance where to look.
  const side = cutCri && cutIns ? "both books"
    : cutCri ? "the CRI ledger"
    : cutIns ? "the insurer statement"
    : "";
  const blocked = !auto && monthBeforeFrom(month, activeFrom);

  return (
    <div className="modal-backdrop" role="presentation">
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="rmd-title">
        <h3 id="rmd-title" style={{ marginTop: 0 }}>Which month is this reconciliation for?</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          This sets the <strong>as-of date</strong> shown on the Excel export and
          throughout the app. The reconciliation is already running while you choose.
        </p>

        <label className="radio-row">
          <input type="radio" name="recon-month-mode" checked={auto}
            onChange={() => setAuto(true)} />
          <span>
            <strong>Automatic</strong>
            <br />
            <span className="muted">
              {/* The span shown is the CRI ledger's own (cri_date_span is CRI-only and
                  advisory); the engine's as-of is the last document date across BOTH
                  books, so this describes the rule rather than asserting a date the
                  run might report differently. */}
              {/* "Reconciles everything" is dropped when the floor bites, or the two
                  sentences would contradict each other in the same breath. */}
              {/* A From in force is the binding lower bound under Automatic too, so it
                  is reported INSTEAD of the CRI span — naming the span here while the
                  run starts somewhere else is the confusing case. It is ATTRIBUTED,
                  because the box is pre-filled for floored insurers and calling that
                  the user's date misstates who set the window. */}
              {activeFrom
                ? <>Reconciles from <strong>{prettyDate(activeFrom)}</strong>{" "}
                   ({fromAttribution}), as at the last transaction date.</>
                : criSpan.first && criSpan.last
                ? <>CRI ledger covers {prettyDate(criSpan.first)} – {prettyDate(criSpan.last)}.
                   {floorHides
                     ? <> Reconciles that window, as at the last transaction date.</>
                     : <> Reconciles everything, as at the last transaction date.</>}</>
                : <>Reconciles everything in the files, as at the last transaction date.</>}
              {floorNote}
            </span>
          </span>
        </label>

        <label className="radio-row">
          <input type="radio" name="recon-month-mode" checked={!auto}
            onChange={() => setAuto(false)} />
          <span>
            <strong>Specific month</strong>
            <br />
            {/* The month sets the CEILING only: period_override bounds period_end and
                never period_from, so the From in force survives the pick unchanged —
                attributed the same way as under Automatic, so the two branches cannot
                tell finance different stories about who set the window. */}
            <span className="muted">Reconcile as at that month-end.
              {activeFrom && <> The start date ({prettyDate(activeFrom)} —{" "}
                {fromAttribution}) still applies.</>}</span>
          </span>
        </label>

        {!auto && (
          <div style={{ marginLeft: 26, marginTop: 8 }}>
            <select className="input" value={month} aria-label="Reconciliation month"
              onChange={(e) => setMonth(e.target.value)}>
              {months.map((m) => (
                <option key={m} value={m} disabled={monthBeforeFrom(m, activeFrom)}>
                  {monthLabel(m)}{monthBeforeFrom(m, activeFrom) ? " (before start date)" : ""}
                </option>
              ))}
            </select>
            {blocked ? (
              <p className="muted" style={{ marginTop: 6, color: "var(--risk)" }}>
                {monthLabel(month)} ends before the start date ({prettyDate(activeFrom)}), so no
                transaction would fall in the period. Pick a later month or change the
                Transaction Period From.
              </p>
            ) : cutoff && (
              <p className="muted" style={{ marginTop: 6 }}>
                As-of date: <strong>{prettyDate(cutoff)}</strong>
                {/* CEILING clause. "no rows are excluded" is an absolute claim, so it may
                    only stand when the floor is NOT also trimming the bottom of the window
                    — otherwise the dialog reassures while rows are being floored out (the
                    AL WATHBA case: nothing trimmed at the top, 10 rows held below). */}
                {trims
                  ? <> — transactions after this are <strong>excluded</strong> from <strong>{side}</strong>, so the balances will change.</>
                  : bothKnown
                    ? (floorHides
                        ? <> — on/after the last transaction in both books.</>
                        : <> — this is on/after the last transaction in both books, so <strong>no rows are excluded</strong>.</>)
                    : <> — transactions after this are <strong>excluded</strong>.</>}
                {/* FLOOR clause, independent of the ceiling: picking a month bounds only
                    period_end (period_override never sets period_from), so cri_period_floor
                    still applies here exactly as it does under Automatic. */}
                {floorNote}
              </p>
            )}
          </div>
        )}

        {/* Progress belongs OUTSIDE the button: the confirm must always read as an
            action the user can take now. Labelling it "Running…" made the dialog look
            blocked, which defeats the point of asking while the engine works. */}
        {running && (
          <p className="muted" style={{ marginTop: 18, marginBottom: 0 }} aria-live="polite">
            {stageText || "Reconciling…"} — confirming now shows the result as soon as it lands.
          </p>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button ref={confirmRef} className="btn btn-primary" disabled={blocked}
            onClick={() => onConfirm(chosen)}>
            Show results
          </button>
        </div>
      </div>
    </div>
  );
}
